import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useSelector } from "react-redux";
import {
  fetchAILiteracyReport,
  fetchProficiencyPercentageData,
  fetchProficiencyRaceDistributionData,
} from "../../services/adminService";
import AdminDashboardCard from "../Admin/AdminDashboardCard";
import SecondaryLoader from "../SecondaryLoader/SecondaryLoader";
import WelcomeCard from "../WelcomeCard";
import TotalUsersIcon from "../../images/svgs/TotalUsersIcon";
import BarChartComponent from "../barChart/BarChart";
import { raceColors } from "../../constants/constants";
import KeyInsights from "../keyInsights";

const AILiteracyReport = () => {
  const userId = useSelector((state) => state.auth.id);

  const {
    data: aiLiteracyReportData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["aiLiteracyReportData"],
    queryFn: fetchAILiteracyReport,
    enabled: !!userId,
  });

  const beginners = aiLiteracyReportData?.Beginner || 0;
  const emerging = aiLiteracyReportData?.Emerging || 0;
  const proficients = aiLiteracyReportData?.Proficient || 0;
  const advanced = aiLiteracyReportData?.Advanced || 0;

  const cardDetails = [
    {
      label: "BEGINNERS",
      figure: beginners,
      icon: <TotalUsersIcon />,
    },
    {
      label: "EMERGING",
      figure: emerging,
      icon: <TotalUsersIcon />,
    },
    {
      label: "PROFICIENT",
      figure: proficients,
      icon: <TotalUsersIcon />,
    },
    {
      label: "ADVANCED",
      figure: advanced,
      icon: <TotalUsersIcon />,
    },
  ];

  const {
    data: proficiencyRaceDistributionData,
    isLoading: proficiencyRaceDistributionLoading,
    error: proficiencyRaceDistributionError,
  } = useQuery({
    queryKey: ["proficiencyRaceDistributionData"],
    queryFn: fetchProficiencyRaceDistributionData,
    enabled: !!userId,
  });

  // 2. Get only the races that appear in the response
  const getUsedRaceLabels = (data) => {
    const raceSet = new Set();

    Object.values(data || {}).forEach((races) => {
      Object.keys(races).forEach((race) => {
        raceSet.add(race);
      });
    });

    return Array.from(raceSet);
  };

  // 3. Transform data into chart format
  const transformStackedBarData = (data) => {
    if (!data) return [];

    const usedRaceLabels = getUsedRaceLabels(data);

    return Object.entries(data).map(([level, races]) => {
      const raceCounts = {};

      usedRaceLabels.forEach((race) => {
        raceCounts[race] = races[race] || 0;
      });

      return {
        name: level,
        ...raceCounts,
      };
    });
  };

  // 4. Generate bars dynamically for present races only
  const usedRaceLabels = getUsedRaceLabels(proficiencyRaceDistributionData);

  const barChartData = transformStackedBarData(proficiencyRaceDistributionData);

  const barChartBars = usedRaceLabels.map((race) => ({
    dataKey: race,
    color: raceColors[race] || "#D3D3D3", // fallback to grey
    label: race,
  }));

  const {
    data: profiicienyByPercentageData,
    isLoading: profiicienyByPercentageLoading,
    error: profiicienyByPercentageError,
  } = useQuery({
    queryKey: ["profiicienyByPercentageData"],
    queryFn: fetchProficiencyPercentageData,
    enabled: !!userId,
  });

  return (
    <div className="flex flex-col gap-6">
      <WelcomeCard title={"AI Literacy Report"} />
      <>
        {" "}
        {isLoading ? (
          <SecondaryLoader />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {cardDetails.map((card, index) => (
                <AdminDashboardCard
                  key={index}
                  label={card.label}
                  figure={card.figure}
                  description={card.description}
                  icon={card.icon}
                />
              ))}
            </div>
            {proficiencyRaceDistributionLoading ? (
              <SecondaryLoader />
            ) : proficiencyRaceDistributionError ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl shadow-sm mb-4">
                <strong className="font-semibold">Oops!</strong> Something went
                wrong while loading race distribution data.
              </div>
            ) : !barChartData.length ? (
              <div className="bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded-xl shadow-sm mb-4">
                <strong className="font-semibold">Heads up!</strong> No quiz
                race distribution data available yet.
              </div>
            ) : (
              <div className="w-full">
                <BarChartComponent
                  title="AI Literacy Breakdown by Race"
                  data={barChartData}
                  bars={barChartBars}
                  height={300}
                />
              </div>
            )}
            {profiicienyByPercentageData && (
              <KeyInsights
                overviewDescription={`${profiicienyByPercentageData.totalUsers} users, ${profiicienyByPercentageData.emerging}% are at the Emerging level of AI literacy, ${profiicienyByPercentageData.beginner}% at Beginner, ${profiicienyByPercentageData.proficient}% at Proficient, and only ${profiicienyByPercentageData.advanced}% at Advanced level.`}
                categoryDescription={
                  "There are notable differences in AI literacy levels across race groups. IT professionals show higher proficiency levels, while education professionals have a higher percentage of beginners despite their crucial role in teaching future generations"
                }
                recommendationDescription={
                  "Develop specialized AI literacy programs for educators and healthcare professionals, and consider creating more advanced courses for IT professionals looking to move from Proficient to Advanced levels."
                }
              />
            )}
          </>
        )}
      </>
    </div>
  );
};

export default AILiteracyReport;
